import {
  Badge,
  Box,
  Button,
  Fieldset,
  Flex,
  Grid,
  NumberInput,
  ScrollArea,
  Select,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconCheck, IconExclamationCircle } from "@tabler/icons-react";
import cx from "clsx";
import { memo, useCallback, useEffect, useState } from "react";
import {
  repositoryDelivery,
  repositoryMdm,
  repositoryPos,
} from "../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { getValueById } from "../../../_base/helper/FunctionHelper";
import { MessageResponse } from "../../../model/MessageResponse";
import { getProvinceSelectNew } from "../../../service/getSelectApi";
import classes from "../../../Styles/TableScrollArea.module.css";

interface SelectModel {
  label: string;
  value: string;
}

interface CustomerDataModel {
  name: string;
  tel: string;
  address: string;
  province: string;
  ward: string;
  amountRemaining: number;
  deposit: number;
  amountTotal: number;
  typeShip: string;
  attribute24: string;
}

interface BranchDeliveryModel {
  orgId: number;
  pick_tel: string;
  pick_address: string;
  pick_province: string;
  pick_ward: string;
  pick_address_detail: string;
}

// Khối lượng đóng gói vượt ngưỡng này thì phải nhập kích thước (dài × rộng × cao)
const WEIGHT_REQUIRE_DIMENSION = 20;

// Khối lượng thực tế tối thiểu (kg); khối lượng đóng gói tối thiểu = 0.1 × 130%
const MIN_WEIGHT = 0.1;
const MIN_WEIGHT_PACKAGE = 0.13;

type DimensionField = "length" | "width" | "height";

interface Products {
  quantity: number;
  weight: number;
  weightPackage: number;
  product_code: string;
  name: string;
  length: number;
  width: number;
  height: number;
}

// Sản phẩm COD có khối lượng đóng gói > 20kg thì bắt buộc khai kích thước
const isRequireDimension = (item: Products, isCOD?: boolean) =>
  Boolean(isCOD) && Number(item.weightPackage) > WEIGHT_REQUIRE_DIMENSION;

interface ProductRowProps {
  item: Products;
  onUpdateWeight: (
    id: string,
    field: "weight" | "weightPackage",
    value: number,
  ) => void;
  onUpdateDimension: (id: string, field: DimensionField, value: number) => void;
  isCOD?: boolean;
}

const ProductRow = memo(
  ({ item, onUpdateWeight, onUpdateDimension, isCOD }: ProductRowProps) => {
    const requireDimension = isRequireDimension(item, isCOD);

    return (
      <Table.Tr>
        <Table.Td w={100}>{item.product_code}</Table.Td>
        <Table.Td>{item.name}</Table.Td>
        <Table.Td w={100}>{item.quantity}</Table.Td>
        <Table.Td w={700}>
          <Flex direction="column" gap={6}>
            <Flex align="center" gap={10}>
              <Text size="11.5px">Khối lượng thực tế</Text>
              <NumberInput
                placeholder="Khối lượng thực tế"
                value={item.weight}
                // Gọi hàm cập nhật cho weight
                onChange={(val) =>
                  onUpdateWeight(item.product_code, "weight", Number(val))
                }
                hideControls
                allowNegative={false}
                // Nhập nhỏ hơn 0.1 thì khi rời ô sẽ tự đẩy lên 0.1
                min={MIN_WEIGHT}
                error={
                  item.weight < MIN_WEIGHT
                    ? `Tối thiểu ${MIN_WEIGHT} kg`
                    : undefined
                }
                suffix=" kg"
                size="xs"
              />
              {isCOD && (
                <>
                  <Text size="11.5px">
                    × 130% = Khối lượng chênh lệch đóng gói
                  </Text>
                  <NumberInput
                    placeholder="Khối lượng đóng gói"
                    value={item.weightPackage}
                    // Gọi hàm cập nhật cho weightPackage
                    onChange={(val) =>
                      onUpdateWeight(
                        item.product_code,
                        "weightPackage",
                        Number(val),
                      )
                    }
                    hideControls
                    allowNegative={false}
                    min={MIN_WEIGHT_PACKAGE}
                    error={
                      item.weightPackage < MIN_WEIGHT_PACKAGE
                        ? `Tối thiểu ${MIN_WEIGHT_PACKAGE} kg`
                        : undefined
                    }
                    suffix=" kg"
                    size="xs"
                  />
                </>
              )}
            </Flex>
            {requireDimension && (
              <Flex align="center" gap={10}>
                <Text size="11.5px">Chiều dài</Text>
                <NumberInput
                  placeholder="Chiều dài"
                  value={item.length}
                  onChange={(val) =>
                    onUpdateDimension(item.product_code, "length", Number(val))
                  }
                  hideControls
                  allowNegative={false}
                  suffix=" cm"
                  size="xs"
                  error={!item.length || item.length <= 0}
                />
                <Text size="11.5px">Chiều rộng</Text>
                <NumberInput
                  placeholder="Chiều rộng"
                  value={item.width}
                  onChange={(val) =>
                    onUpdateDimension(item.product_code, "width", Number(val))
                  }
                  hideControls
                  allowNegative={false}
                  suffix=" cm"
                  size="xs"
                  error={!item.width || item.width <= 0}
                />
                <Text size="11.5px">Chiều cao</Text>
                <NumberInput
                  placeholder="Chiều cao"
                  value={item.height}
                  onChange={(val) =>
                    onUpdateDimension(item.product_code, "height", Number(val))
                  }
                  hideControls
                  allowNegative={false}
                  suffix=" cm"
                  size="xs"
                  error={!item.height || item.height <= 0}
                />
              </Flex>
            )}
          </Flex>
        </Table.Td>
      </Table.Tr>
    );
  },
);

const ConfirmInfoGHTK = ({
  orderNumber,
  onSuccess,
  isCOD,
}: {
  orderNumber: string;
  onSuccess: any;
  isCOD?: boolean;
}) => {
  const [isSuccess, setIsSuccess] = useState<boolean>(true);
  const [scrolled, setScrolled] = useState(false);

  const [orderHeader, setOrderHeader] = useState<any>({});
  const [customerData, setCustomerData] = useState<CustomerDataModel>({
    name: "",
    tel: "",
    address: "",
    province: "",
    ward: "",
    amountRemaining: 0,
    deposit: 0,
    amountTotal: 0,
    typeShip: "",
    attribute24: "",
  });

  const [branchDeliveryData, setBranchDeliveryData] =
    useState<BranchDeliveryModel>({
      orgId: 0,
      pick_tel: "",
      pick_address: "",
      pick_province: "",
      pick_ward: "",
      pick_address_detail: "",
    });

  const [dataProducts, setDataProducts] = useState<Products[]>([]);

  const [dataProvinceNew, setDataProvinceNew] = useState<SelectModel[]>([]);
  const [dataCommuneNewKH, setDataCommuneNewKH] = useState<SelectModel[]>([]);
  const [dataCommuneNewCN, setDataCommuneNewCN] = useState<SelectModel[]>([]);
  const [dataBranch, setDataBranch] = useState<any[]>([]);
  const [totalBox, setTotalBox] = useState<number>(1);
  const [titleError, setTitleError] = useState<string>("");

  const customHeightTable = () => {
    // Mỗi sản phẩm phải khai kích thước sẽ chiếm thêm 1 dòng nhập liệu
    const extraHeight =
      dataProducts.filter((i) => isRequireDimension(i, isCOD)).length * 40;

    if (dataProducts.length <= 1 || !dataProducts) {
      return 110 + extraHeight;
    } else if (dataProducts.length === 2) {
      return 150 + extraHeight;
    } else if (dataProducts.length === 3) {
      return 190 + extraHeight;
    } else if (dataProducts.length === 4) {
      return 230 + extraHeight;
    } else {
      return 270 + extraHeight;
    }
  };

  const validateSpecificFields = (
    data: any,
    fieldsToCheck?: string[],
  ): boolean => {
    const key = !fieldsToCheck ? Object.keys(data) : fieldsToCheck;

    for (const field of key) {
      const value = data[field];
      if (!(field in data)) {
        return false;
      }
      if (value === null || value === undefined) {
        return false;
      }
      if (typeof value === "string" && value.trim() === "") {
        return false;
      }
      if (typeof value === "number" && value === 0) {
        return false;
      }
    }

    return true;
  };

  const fetchDataProvinceNew = async () => {
    const res = await getProvinceSelectNew();

    if (res) {
      setDataProvinceNew(
        res
          ?.filter((item) => item.id && item.name)
          ?.map((item) => ({
            value: String(item.id),
            label: String(item.name),
          })),
      );
    }
  };

  const fetchDataCommuneNew = async (idProvince: string, type: string) => {
    const dataSearch =
      idProvince && idProvince.length > 0 ? `&ProvinceIds=${idProvince}` : "";

    const res = await repositoryMdm.get<MessageResponse<any>>(
      `/api/v1/TblDmCommuneMoi/get-select?Active=true${dataSearch}`,
    );

    if (res && res.success) {
      const result = res.data;
      const dataSet = result
        ?.filter((item: any) => item.text && item.value)
        ?.map((item: any) => ({
          value: String(item.value),
          label: String(item.text),
        }));
      if (type === "KH") {
        setDataCommuneNewKH(dataSet);
      } else if (type === "CN") {
        setDataCommuneNewCN(dataSet);
      }
    }
  };

  const fetchDataBranch = async (idOrg: number) => {
    const res = await repositoryDelivery.get<MessageResponse<any>>(
      "/api/v1/TblDmMasterOrg/get-all",
    );

    if (res && res.success) {
      const data = res.data;
      setDataBranch(data);
      if (idOrg) {
        setBranchDeliveryData({
          orgId: idOrg,
          pick_tel:
            data.find((i: any) => String(i.id) === String(idOrg))
              ?.attribute11 ?? "",
          pick_address:
            data.find((i: any) => String(i.id) === String(idOrg))?.address ??
            "",
          pick_province:
            data.find((i: any) => String(i.id) === String(idOrg))?.attribute8 ??
            "",
          pick_ward:
            data.find((i: any) => String(i.id) === String(idOrg))?.attribute9 ??
            "",
          pick_address_detail:
            data.find((i: any) => String(i.id) === String(idOrg))
              ?.attribute10 ?? "",
        });
      }
    }
  };

  const getDataOrder = async () => {
    const url = `/api/v1/CreateSellItem/details?orderNumber=${orderNumber}`;
    const response = await repositoryPos.get<MessageResponse<any>>(url);

    if (response && response?.success) {
      getDataProductWeight(response?.data?.orderDetail);

      const customerData = JSON.parse(response.data?.orderHeader?.attribute1);

      setCustomerData({
        name: customerData.customerName ?? "",
        tel: customerData.telephoneNumber ?? "",
        address: customerData.customerAddress ?? "",
        province: String(customerData.customerProvinceId) ?? "",
        ward: String(customerData.customerCommuneId) ?? "",
        amountRemaining: response.data?.orderHeader.amountRemaining ?? 0,
        deposit: response.data?.orderHeader.deposit ?? 0,
        amountTotal: response.data?.orderHeader.amountTotal ?? 0,
        typeShip: customerData?.typeShip ?? "",
        attribute24: response.data?.orderHeader?.attribute24 ?? "",
      });
      setOrderHeader(customerData);
      fetchDataBranch(response.data?.orderHeader?.orgId ?? 0);

      if (
        response.data?.orderHeader?.shipingVendor !== "Giao hàng tiết kiệm" &&
        !isCOD
      ) {
        NotificationExtension.Fails("Đơn hàng không phải GHTK !");
        setTitleError("Đơn hàng không phải GHTK");
        setIsSuccess(false);
      }

      if (response.data?.orderHeader?.shipingVendor !== "COD" && isCOD) {
        NotificationExtension.Fails("Đơn hàng không phải COD !");
        setTitleError("Đơn hàng không phải COD !");
        setIsSuccess(false);
      }

      if (
        response.data?.orderHeader?.attribute24 === "Giao Hàng Thu Tiền(COD)" &&
        (response.data?.orderHeader.amountRemaining === 0 ||
          !response.data?.orderHeader.amountRemaining) &&
        isCOD
      ) {
        NotificationExtension.Fails(
          "Đơn hàng này không có COD, không thể tạo đơn GHTK COD !",
        );
        setTitleError(
          "Đơn hàng này không có COD, không thể tạo đơn GHTK COD !",
        );
        setIsSuccess(false);
      }
    }
  };

  const getDataProductWeight = async (listProduct: any[]) => {
    let url = "/api/v1/TblItem/get-attribute54-by-item-codes";

    if (listProduct) {
      url += "?" + listProduct.map((i) => `ItemCodes=${i.itemCode}`).join("&");
    }

    const res = await repositoryPos.get<MessageResponse<any>>(url);

    if (res && res.success) {
      const data = res.data;

      const products = listProduct.map((item: any) => ({
        quantity: item.quality,
        // API trả khối lượng < 0.1kg (hoặc không có) thì đẩy lên mức tối thiểu
        weight: Math.max(
          Number(
            data.find((i: any) => i.itemCode === item.itemCode)?.attribute54,
          ) / 1000 || 0,
          MIN_WEIGHT,
        ),
        weightPackage: Math.max(
          Number(
            Number(
              data.find((i: any) => i.itemCode === item.itemCode)?.attribute54 *
                130,
            ) / 100,
          ) / 1000 || 0,
          MIN_WEIGHT_PACKAGE,
        ),
        product_code: item.itemId,
        name: item.itemName,
        length: 0,
        width: 0,
        height: 0,
      }));
      setDataProducts(products);
    }
  };

  const handleUpdateWeight = useCallback(
    (id: string, field: "weight" | "weightPackage", value: number) => {
      setDataProducts((prevData) =>
        prevData.map((item) => {
          if (item.product_code === id) {
            const numValue = Number(value) || 0;

            if (field === "weight") {
              return {
                ...item,
                weight: numValue,
                weightPackage: Number((numValue * 1.3).toFixed(2)),
              };
            }

            if (field === "weightPackage") {
              return {
                ...item,
                weightPackage: numValue,
                weight: Number(((numValue * 100) / 130).toFixed(2)),
              };
            }

            return { ...item, [field]: value };
          }
          return item;
        }),
      );
    },
    [],
  );

  const handleUpdateDimension = useCallback(
    (id: string, field: DimensionField, value: number) => {
      setDataProducts((prevData) =>
        prevData.map((item) =>
          item.product_code === id
            ? { ...item, [field]: Number(value) || 0 }
            : item,
        ),
      );
    },
    [],
  );

  const handeOrderGHTK = async () => {
    if (!customerData.typeShip || !customerData.typeShip.trim()) {
      NotificationExtension.Fails("Thiếu loại vận chuyển (Hỏa tốc/Thường)");
      return;
    }

    if (
      !dataProducts ||
      dataProducts?.filter((i: any) => i)?.length === 0 ||
      dataProducts
        ?.filter((i: any) => i)
        ?.some(
          (i: any) =>
            !i?.weight ||
            i?.weight < MIN_WEIGHT ||
            String(i?.weight).length === 0,
        )
    ) {
      NotificationExtension.Fails(
        `Sản phẩm có ID ${dataProducts
          ?.filter(
            (i: any) =>
              !i?.weight ||
              i?.weight < MIN_WEIGHT ||
              String(i?.weight).length === 0,
          )
          ?.map((i: any) => i?.product_code)
          ?.join(
            ", ",
          )} có khối lượng không hợp lệ (tối thiểu ${MIN_WEIGHT}kg) !`,
      );
      return;
    }

    const productsMissingDimension = dataProducts.filter(
      (i) =>
        isRequireDimension(i, isCOD) &&
        (!i.length ||
          i.length <= 0 ||
          !i.width ||
          i.width <= 0 ||
          !i.height ||
          i.height <= 0),
    );

    if (productsMissingDimension.length > 0) {
      NotificationExtension.Fails(
        `Sản phẩm có ID ${productsMissingDimension
          ?.map((i) => i?.product_code)
          ?.join(
            ", ",
          )} có khối lượng đóng gói lớn hơn ${WEIGHT_REQUIRE_DIMENSION}kg, cần nhập đủ chiều dài, chiều rộng, chiều cao !`,
      );
      return;
    }

    const normalizedPayType = (customerData.attribute24 || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
    const isDebtOrder = normalizedPayType.includes("cong no");

    const processedProducts = dataProducts.map((item) => {
      const { weightPackage, length, width, height, ...rest } = item;

      return {
        ...rest,
        weight: !isCOD ? item.weight : item.weightPackage,
        // Chỉ gửi kích thước với sản phẩm có khối lượng đóng gói vượt ngưỡng
        ...(isRequireDimension(item, isCOD)
          ? { length: length, width: width, height: height }
          : {}),
      };
    });

    const ghtkPayload = {
      // Token GHTK do server giữ, FE chỉ báo đây có phải đơn thu hộ hay không
      isCod: isCOD,
      order: {
        products: processedProducts,
        order: {
          id: orderNumber,
          pick_name: "Nội thành Hà Nội",
          pick_address: branchDeliveryData?.pick_address,
          pick_province:
            getValueById(
              String(branchDeliveryData.pick_province),
              dataProvinceNew,
              "label",
            ) || null,
          pick_district: null,
          pick_ward:
            getValueById(
              String(branchDeliveryData.pick_ward),
              dataCommuneNewCN,
              "label",
            ) || null,
          pick_tel: branchDeliveryData.pick_tel,
          tel: customerData.tel,
          name: customerData.name,
          address: [
            customerData.address,
            getValueById(String(customerData.ward), dataCommuneNewKH, "label"),
            getValueById(
              String(customerData.province),
              dataProvinceNew,
              "label",
            ),
          ]
            .filter((i) => i && String(i).length > 0)
            .join(" - "),
          province:
            getValueById(
              String(customerData.province),
              dataProvinceNew,
              "label",
            ) || null,
          district: null,
          ward:
            getValueById(
              String(customerData.ward),
              dataCommuneNewKH,
              "label",
            ) || null,
          hamlet: "Khác",
          // Nếu khách đã thanh toán (amountRemaining = 0) thì không thu phí ship của khách
          is_freeship:
            !customerData.amountRemaining ||
            customerData.amountRemaining <= 0 ||
            isDebtOrder
              ? "1"
              : isCOD
                ? "1"
                : "1",
          pick_money:
            customerData.attribute24 === "Giao Hàng Thu Tiền(COD)"
              ? (customerData.amountRemaining || 0) -
                (customerData.deposit || 0)
              : 0,
          note: "",
          value: customerData.amountTotal,
          pick_option: "cod",
          total_box: totalBox,
        },
      },
      // Cập nhật thông tin giao vận vào đơn hàng nằm luôn trong API này,
      // chạy chung transaction với việc đẩy sang GHTK
      updateOrderShipping: isCOD,
      attribute78: "DA_DONG_GOI",
      attribute71: "Đẩy lại đơn GHTK",
    };

    console.log("GHTK Full Payload:", JSON.stringify(ghtkPayload, null, 2));

    const responseGHTK = await repositoryMdm.post<any>(
      "/api/v1/GHTK/create-order",
      ghtkPayload,
    );
    if (responseGHTK?.success === true) {
      // Mã tra cứu đơn giờ do API create-order tự ghi vào attribute1,
      // chạy chung transaction với phần còn lại nên không cần gọi thêm ở đây
      NotificationExtension.Success("Tạo đơn GHTK thành công");

      setTimeout(() => {
        modals.closeAll();
        onSuccess();
      }, 1000);
    } else {
      setTimeout(() => {
        NotificationExtension.Fails(
          `Tạo đơn GHTK thất bại: ${responseGHTK?.message}`,
        );
      }, 1000); // Delay 200ms

      // Ghi log lỗi vào hệ thống
      await repositoryPos.post("/api/v1/CreateSellItem/update-ghtk-log", {
        orderNumber: orderNumber,
        attribute42:
          responseGHTK?.message || "Lỗi không xác định khi tạo đơn GHTK",
      });
    }
  };

  useEffect(() => {
    if (orderNumber && String(orderNumber).length > 0) {
      getDataOrder();
      fetchDataProvinceNew();
    } else {
      setIsSuccess(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    if (customerData.province && customerData.province.length > 0) {
      fetchDataCommuneNew(customerData.province, "KH");
    }
  }, [customerData.province]);

  useEffect(() => {
    if (
      branchDeliveryData.pick_province &&
      branchDeliveryData.pick_province.length > 0
    ) {
      fetchDataCommuneNew(branchDeliveryData.pick_province, "CN");
    }
  }, [branchDeliveryData.pick_province]);

  return (
    <Box maw={1550} w={"92.5vw"}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Fieldset legend={<Badge>Thông tin khách hàng</Badge>} mt={10}>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label={"Tên khách hàng"}
                  placeholder="Nhập tên khách hàng"
                  variant={!isSuccess ? "filled" : "default"}
                  readOnly={!isSuccess}
                  value={customerData.name}
                  onChange={(e) =>
                    setCustomerData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  withAsterisk
                  error={
                    !customerData.name || customerData.name.length === 0
                      ? "Tên khách hàng không được bỏ trống !"
                      : false
                  }
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label={"Số điện thoại"}
                  placeholder="Nhập số điện thoại"
                  type="number"
                  variant={!isSuccess ? "filled" : "default"}
                  readOnly={!isSuccess}
                  value={customerData.tel}
                  onChange={(e) => {
                    setCustomerData((prev) => ({
                      ...prev,
                      tel: e.target.value,
                    }));
                  }}
                  withAsterisk
                  error={
                    !customerData.tel || customerData.tel.length === 0
                      ? "SĐT khách hàng không được bỏ trống !"
                      : false
                  }
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Tỉnh/TP (Khách hàng)"
                  placeholder="Nhập Tỉnh/TP"
                  data={dataProvinceNew}
                  value={
                    customerData.province &&
                    String(customerData.province).length > 0
                      ? customerData.province
                      : null
                  }
                  searchable
                  clearable
                  nothingFoundMessage="Không tìm thấy dữ liệu !"
                  variant={!isSuccess ? "filled" : "default"}
                  readOnly={!isSuccess}
                  onChange={(e) =>
                    setCustomerData((prev) => ({
                      ...prev,
                      province: e ? e : "",
                      ward: "",
                    }))
                  }
                  withAsterisk
                  error={
                    !customerData.province || customerData.province.length === 0
                      ? "Tỉnh/TP không được bỏ trống !"
                      : false
                  }
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Phường/xã (Khách hàng)"
                  placeholder="Nhập Phường/xã"
                  data={dataCommuneNewKH}
                  value={
                    customerData.ward && String(customerData.ward).length > 0
                      ? customerData.ward
                      : null
                  }
                  searchable
                  clearable
                  nothingFoundMessage="Không tìm thấy dữ liệu !"
                  variant={
                    !isSuccess ||
                    !customerData.province ||
                    String(customerData.province).length === 0
                      ? "filled"
                      : "default"
                  }
                  readOnly={
                    !isSuccess ||
                    !customerData.province ||
                    String(customerData.province).length === 0
                  }
                  onChange={(e) =>
                    setCustomerData((prev) => ({
                      ...prev,
                      ward: e ? e : "",
                    }))
                  }
                  withAsterisk
                  error={
                    !customerData.ward || customerData.ward.length === 0
                      ? "Phường/xã không được bỏ trống !"
                      : false
                  }
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="Địa chỉ cụ thể ( Không bao gồm Tỉnh/TP, Phường/Xã )"
                  placeholder="Nhập địa chỉ cụ thể"
                  variant={!isSuccess ? "filled" : "default"}
                  readOnly={!isSuccess}
                  value={customerData.address}
                  onChange={(e) => {
                    setCustomerData((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }));
                  }}
                  withAsterisk
                  error={
                    !customerData.address || customerData.address.length === 0
                      ? "Địa chỉ khách hàng không được bỏ trống !"
                      : false
                  }
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="Địa chỉ đầy đủ"
                  placeholder="Địa chỉ đầy đủ"
                  value={[
                    customerData.address,
                    getValueById(
                      String(customerData.ward),
                      dataCommuneNewKH,
                      "label",
                    ),
                    getValueById(
                      String(customerData.province),
                      dataProvinceNew,
                      "label",
                    ),
                  ]
                    .filter((i) => i && String(i).length > 0)
                    .join(" - ")}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
            </Grid>
          </Fieldset>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Fieldset legend={<Badge>Điểm lấy hàng</Badge>} mt={10}>
            <Grid>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Select
                  label="Chi nhánh"
                  placeholder="Chi nhánh"
                  data={dataBranch
                    .filter((i) => i.id && i.code && i.name)
                    .map((i) => ({
                      label: `${i.code} - ${i.name}`,
                      value: String(i.id),
                    }))}
                  value={
                    branchDeliveryData.orgId ||
                    String(branchDeliveryData.orgId).length === 0
                      ? String(branchDeliveryData.orgId)
                      : ""
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <TextInput
                  label="SĐT liên hệ lấy hàng của chi nhánh"
                  placeholder="SĐT"
                  type="number"
                  value={branchDeliveryData.pick_tel}
                  withAsterisk
                  error={
                    !branchDeliveryData.pick_tel ||
                    branchDeliveryData.pick_tel.length === 0
                      ? "Không tìm thấy SĐT liên hệ !"
                      : false
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <NumberInput
                  label="Số kiện hàng"
                  placeholder="Nhập số kiện hàng"
                  min={1}
                  value={totalBox}
                  onChange={(val) => setTotalBox(Number(val) || 1)}
                  withAsterisk
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Tỉnh/TP (Chi nhánh)"
                  placeholder="Tỉnh/TP"
                  data={dataProvinceNew}
                  value={
                    branchDeliveryData.pick_province &&
                    String(branchDeliveryData.pick_province).length > 0
                      ? branchDeliveryData.pick_province
                      : null
                  }
                  withAsterisk
                  error={
                    !branchDeliveryData.pick_province ||
                    branchDeliveryData.pick_province.length === 0
                      ? "Không tìm thấy Tỉnh/TP !"
                      : null
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Phường/xã (Chi nhánh)"
                  placeholder="Phường/xã"
                  data={dataCommuneNewCN}
                  value={
                    branchDeliveryData.pick_ward &&
                    String(branchDeliveryData.pick_ward).length > 0
                      ? branchDeliveryData.pick_ward
                      : null
                  }
                  withAsterisk
                  error={
                    !branchDeliveryData.pick_ward ||
                    branchDeliveryData.pick_ward.length === 0
                      ? "Không tìm thấy phường/xã !"
                      : false
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="Địa chỉ cụ thể"
                  placeholder="Địa chỉ cụ thể"
                  value={branchDeliveryData.pick_address_detail}
                  error={
                    !branchDeliveryData.pick_address_detail ||
                    branchDeliveryData.pick_address_detail.length === 0
                      ? "Không tìm thấy địa chỉ cụ thể !"
                      : false
                  }
                  variant="filled"
                  readOnly
                  withAsterisk
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="Địa chỉ chi nhánh"
                  placeholder="Địa chỉ chi nhánh"
                  value={branchDeliveryData.pick_address}
                  withAsterisk
                  error={
                    !branchDeliveryData.pick_address ||
                    branchDeliveryData.pick_address.length === 0
                      ? "Không tìm thấy địa chỉ cụ thể !"
                      : null
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
            </Grid>
          </Fieldset>
        </Grid.Col>
      </Grid>
      <ScrollArea
        h={customHeightTable()}
        onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
      >
        <Table
          striped
          highlightOnHover
          withTableBorder
          withColumnBorders
          mt={10}
        >
          <Table.Thead
            className={cx(classes.header, {
              [classes.scrolled]: scrolled,
            })}
          >
            <Table.Tr>
              <Table.Th>ID sản phẩm</Table.Th>
              <Table.Th>Tên sản phẩm</Table.Th>
              <Table.Th>Số lượng</Table.Th>
              <Table.Th>Khối lượng (kg)</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {dataProducts && dataProducts.length > 0 ? (
              dataProducts.map((i: Products) => (
                <ProductRow
                  key={i.product_code}
                  item={i}
                  onUpdateWeight={handleUpdateWeight}
                  onUpdateDimension={handleUpdateDimension}
                  isCOD={isCOD}
                />
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={4} ta={"center"}>
                  Không tìm thấy sản phẩm !
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      <Flex
        justify={
          titleError && titleError.length > 0 ? "space-between" : "center"
        }
        align={"center"}
        mt={5}
        gap={50}
      >
        {titleError && titleError.length > 0 && (
          <Box
            py={5}
            px={10}
            style={{
              backgroundColor: "#f6f8fa",
              borderRadius: "6px",
              borderLeft: "3px solid red",
            }}
            flex={1}
          >
            <Flex align={"center"} justify={"space-between"}>
              <Text size="sm" fw={"bold"} c="dark">
                {titleError}
              </Text>
              <IconExclamationCircle color="red" />
            </Flex>
          </Box>
        )}
        <Button
          leftSection={<IconCheck size={16} />}
          disabled={
            !isSuccess ||
            !validateSpecificFields(customerData, [
              "name",
              "tel",
              "address",
              "province",
              "ward",
              "typeShip",
            ]) ||
            !validateSpecificFields(branchDeliveryData) ||
            !dataProducts ||
            dataProducts.length === 0
          }
          onClick={() => {
            handeOrderGHTK();
          }}
        >
          Xác nhận
        </Button>
      </Flex>
    </Box>
  );
};

export default ConfirmInfoGHTK;
